import React, { memo, useRef } from 'react';
import type { ProductOffer, ProductCatalogStyle, ProductLayout } from '@segmently/schema-models/v2';
import type { TemplateVariables } from '../../../utils/contentResolver';
import type { PreviewMode } from '../../../types/previewMode';
import { useElementAnimation, useSelectionAnimation, mergeAnimationClassName } from '../../../animations';
import type { ProductOfferEventContext } from './variants/types';
import { CheckboxLabels, LabelsCheckbox, Labels } from './variants';

export interface ProductOfferItemProps {
  item: ProductOffer;
  productLayout?: ProductLayout;
  index: number;
  locale: string;
  variables?: TemplateVariables;
  selected?: boolean;
  catalogAppearance?: ProductCatalogStyle;
  selectedCatalogAppearance?: ProductCatalogStyle;
  onSelect?: (context: ProductOfferEventContext) => void;
  className?: string;
  'data-testid'?: string;
  editPath?: string;
  previewMode?: PreviewMode;
  layout?: 'vertical' | 'horizontal';
  /** 0-based display position — feeds per-item entrance stagger */
  staggerIndex?: number;
}

export const ProductOfferItem = memo(function ProductOfferItem({
  item,
  productLayout,
  index,
  locale,
  variables,
  selected = false,
  catalogAppearance,
  selectedCatalogAppearance,
  onSelect,
  className,
  'data-testid': testId,
  editPath,
  previewMode,
  layout = 'vertical',
  staggerIndex,
}: ProductOfferItemProps): React.ReactElement | null {
  const entranceAnim = useElementAnimation(editPath, { staggerIndex });
  const selectionAnim = useSelectionAnimation(editPath, selected);
  // Selection feedback replaces the entrance class while active, and once it
  // has shown we never fall back to the entrance (re-adding the class would
  // restart the CSS entrance on deselect) — same contract as OptionsItem.
  const selectionSeenRef = useRef(false);
  if (selectionAnim.className) selectionSeenRef.current = true;
  const itemAnim = selectionAnim.className
    ? selectionAnim
    : (selectionSeenRef.current ? {} : entranceAnim);

  const variantProps = {
    item,
    index,
    locale,
    variables,
    selected,
    catalogAppearance,
    selectedCatalogAppearance,
    onSelect,
    className: mergeAnimationClassName(className, itemAnim),
    style: itemAnim.style,
    'data-testid': testId,
    editPath,
    previewMode,
    layout,
  };

  switch (productLayout) {
    case 'CheckboxLabels':
      return <CheckboxLabels {...variantProps} />;

    case 'LabelsCheckbox':
      return <LabelsCheckbox {...variantProps} />;

    default:
      return <Labels {...variantProps} />;
  }
});

ProductOfferItem.displayName = 'ProductOfferItem';
